import { getTheme } from "PFApp/theme_context_provider/get_theme";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag } from "PFTypes";
import React, { PropsWithChildren, useEffect, useState } from "react";

import { Theme } from "./theme_context_provider.types";
import { ThemeContext } from "./theme_context_provider_context";

export const ThemeContextProvider = ({ children }: PropsWithChildren) => {
  const isThemeEnabled = useIsFeatureEnabled(FeatureFlag.DarkMode);

  const [theme, setTheme] = useState<Theme>(getTheme(isThemeEnabled));

  useEffect(() => {
    setTheme(getTheme(isThemeEnabled));
    if (!isThemeEnabled) {
      return;
    }
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const newTheme = mediaQuery.matches ? Theme.Dark : Theme.Light;
      const savedTheme = localStorage.getItem("theme") as Theme | null;
      if (!savedTheme) {
        setTheme(newTheme);
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isThemeEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const changeTheme = (newTheme: Theme) => {
    if (Object.values(Theme).includes(newTheme)) {
      setTheme(newTheme);
      localStorage.setItem("theme", newTheme);
    }
  };

  const themes = Object.values(Theme);

  return <ThemeContext.Provider value={{ theme, changeTheme, themes }}>{children}</ThemeContext.Provider>;
};
